package com.taocent.simple.compose.component.richtext.core.internal.clipboard

import androidx.compose.ui.platform.ClipEntry
import androidx.compose.ui.platform.Clipboard
import androidx.compose.ui.text.AnnotatedString
import com.taocent.simple.compose.component.richtext.core.PasteResult
import com.taocent.simple.compose.component.richtext.core.RichTextError
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.serialization.AnnotatedStringToHtml
import com.taocent.simple.compose.component.richtext.core.internal.serialization.RichTextHtmlConfig
import com.taocent.simple.compose.component.richtext.core.internal.serialization.hasRichStyles

/**
 * 上报错误的回调类型 — 由 [RichTextState] 注入,内部模块解耦。
 */
internal typealias ClipboardErrorReporter = (RichTextError, String) -> Unit

/**
 * 内部剪贴板控制器:基于新的 [Clipboard] 接口(suspend API),
 * 在剪贴板读写前后与 [RichTextState] 的 emoji-id 缓冲协议保持同步。
 */
internal class RichTextClipboardManager(
    private val clipboard: Clipboard,
    private val state: RichTextState,
    private val errorReporter: ClipboardErrorReporter? = null,
    private val htmlConfig: RichTextHtmlConfig = RichTextHtmlConfig(),
) {
    /**
     * 写入文本到剪贴板。失败时返回 [PasteResult.Failure],错误同时通过 reporter 上报。
     *
     * 富文本样式策略:
     *   - 有 rich styles / emoji / hyperlink(由 [hasRichStyles] 判定)
     *     → 调 [toRichClipEntry] 同时写 text/html + text/plain(双 MIME)
     *   - 否则只写 text/plain(走 [toClipEntry] 旧路径,无变化)
     *
     * **emoji + 双 MIME 关键设计**:
     * - plain text 用 [RichTextState.getCopyAnnotatedString] 风格的 shortcode 版
     *   (emoji → `[id]` 短码),让外部纯文本应用看到可读短码、不会看到 `█` placeholder
     *   字符(看起来像方块)
     * - text/html 用**原版** annotated(占位符 + emoji annotation)序列化,这样
     *   [AnnotatedStringToHtml] 能识别 emoji annotation 段,输出
     *   `<span data-emoji-id="smile"></span>` 标记 —— 本组件跨进程 paste 时
     *   [HtmlToAnnotatedString.parse] 会反向 append `█` placeholder + annotation,
     *   **emoji 图像可被还原**;外部非本组件 app 看到空 `<span>` 时显示为空白
     *   (emoji 在那些 app 反正不能渲染,这种"空白"行为可接受)
     * - 这两个分离的来源由 [toRichClipEntry] 的 (plainText, html) overload 接收
     */
    suspend fun setText(annotatedString: AnnotatedString): PasteResult<Unit> = try {
        val original = annotatedString
        val clipAnnotated = state.convertEmojiToIdFormat(original)
        val entry = if (hasRichStyles(clipAnnotated)) {
            // 关键修复:plain text 用 shortcode 版(短码可读,外部应用看到 [smile]),
            // html 用**原版** annotated 序列化(让序列化器能识别 emoji annotation 段
            // 输出 <span data-emoji-id>),这样:
            //   - 跨进程 paste 到本组件:toRichAnnotatedStringOrNull 读 text/html →
            //     HtmlToAnnotatedString.parse 看到 data-emoji-id → append █ +
            //     annotation → emoji 还原成功
            //   - 跨进程 paste 到外部纯文本应用:读 text/plain 看到短码 [smile]
            val plain = clipAnnotated.text
            val html = AnnotatedStringToHtml.toHtml(original, htmlConfig)
            toRichClipEntry(plain, html, htmlConfig)
        } else {
            clipAnnotated.toClipEntry()
        }
        // 把 entry 写入系统剪贴板。各 platform 实际写入路径不同 —— 见
        // [setRichTextClipEntry] 的 expect/actual 拆分与 KDoc 解释
        // (iOS 平台**不**调 Compose Clipboard.setClipEntry,避免 `pb.string = ...` 覆盖
        // 前面 toRichClipEntry 已经写入的 UIPasteboard.items 双 MIME;其他平台正常调
        // clipboard.setClipEntry,让 Compose Clipboard API 写入系统剪贴板 + 让
        // FakeClipboard 单元测试也能通过)。
        setRichTextClipEntry(clipboard, entry)
        PasteResult.success(Unit)
    } catch (e: Throwable) {
        val error = RichTextError.ClipboardWrite(e)
        errorReporter?.invoke(error, "RichTextClipboardManager.setText")
        PasteResult.failure(error)
    }

    /**
     * 从剪贴板读取文本。成功时 [PasteResult.Success.value] 为 AnnotatedString(可能为空字符串但不会 null);
     * 剪贴板无内容或权限拒绝时 [PasteResult.Failure]。
     *
     * 富文本读取策略:优先尝试 [toRichAnnotatedStringOrNull](读 text/html → HtmlToAnnotatedString.parse),
     * 失败 / 无 text/html 时 fallback 到 [toAnnotatedStringOrNull](纯 text/plain)。
     *
     * 注意:**完全依赖双 MIME(text/plain + text/html)路径还原 rich styles**。
     * 之前有 `RichTextState.resolveClipboard` 进程内 buffer 命中路径(同进程跨
     * RichTextState 复制粘贴保留 spans)—— 已移除,统一走 text/html 反序列化。
     */
    suspend fun getText(): PasteResult<AnnotatedString> = try {
        val entry = clipboard.getClipEntry()
        if (entry == null) {
            PasteResult.success(AnnotatedString(""))
        } else {
            val text = entry.toRichAnnotatedStringOrNull() ?: entry.toAnnotatedStringOrNull()
            PasteResult.success(text ?: AnnotatedString(""))
        }
    } catch (e: Throwable) {
        val error = RichTextError.ClipboardRead(e)
        errorReporter?.invoke(error, "RichTextClipboardManager.getText")
        PasteResult.failure(error)
    }
}

/**
 * 将 [AnnotatedString] 写入剪贴板前,构造一个携带其纯文本的 [ClipEntry]。
 * public 暴露以便 :richtext / :blockrichtext 内部 UI 使用。
 *
 * 此 expect 只写 text/plain —— 文本/JSON 等纯文本内容走这个路径;
 * 带 rich styles 的富文本请走 [toRichClipEntry]。
 */
expect fun AnnotatedString.toClipEntry(): ClipEntry

/**
 * 富文本版 [toClipEntry]:有 rich styles 的 [AnnotatedString] 写入时**额外**带 text/html 副 MIME,
 * 以便跨平台粘贴时(本组件内部或外部浏览器富文本编辑器)还原 spans / paragraph / emoji / hyperlink。
 *
 * 调用方应在调用前用 [hasRichStyles] 判定,确保只在值得多写一份 HTML 时才走这个路径。
 *
 * 实现要点:
 *   - JVM:`MultiMimeStringSelection` 同时支持 `DataFlavor.stringFlavor` + `DataFlavor.fragmentHtmlFlavor`
 *   - Web (jsMain / wasmJsMain):`ClipboardItem { "text/plain": Blob, "text/html": Blob }` + `navigator.clipboard.write`
 *   - Android:`ClipData.newHtmlText(html, text)`(同一 item 同时含 plain + html)
 *   - iOS:`NSPasteboardItem` 注册 `public.utf8-plain-text` + `public.html`
 */
expect fun AnnotatedString.toRichClipEntry(config: RichTextHtmlConfig = RichTextHtmlConfig()): ClipEntry

/**
 * 富文本版 [toRichClipEntry] 的 low-level 重载:接收分离的 `plainText` + `html` 字符串,
 * 用于"plain text 形态与 text/html 形态来源不同 AnnotatedString"的场景。
 *
 * **典型场景**:`[RichTextClipboardManager.setText]` 写 emoji 时:
 *   - plain text 来自 shortcode 版(emoji → `[id]` 短码)→ 外部纯文本 app 看到短码
 *   - html 来自**原版** annotated(emoji → `█` 占位符 + annotation)→ 序列化器能识别
 *     emoji annotation 段,输出 `<span data-emoji-id="smile"></span>`;本组件跨进程
 *     paste 时反序列化器 append `█` placeholder + annotation,emoji 还原
 *
 * 如果用 `AnnotatedString.toRichClipEntry(annotated, config)`,plain = `annotated.text`、
 * html = `AnnotatedStringToHtml.toHtml(annotated, config)` —— **两者都来自同一个 annotated**。
 * 想要分离来源时,调本重载,内部平台实现直接接收 plain + html 字符串写剪贴板。
 *
 * **签名是 suspend 的原因**:
 *   - Web 平台 [navigator.clipboard.write] 是异步 Promise,实现必须是 suspend
 *   - 其他平台(Android / iOS / JVM)实际写入是同步,`suspend` 不会增加协程 yield 成本
 *   - 这样 `RichTextClipboardManager.setText` 内部只需调 `toRichClipEntry` 一句(已 suspend
 *     上下文中),不需要在 hasRichStyles 路径再走 `clipboard.setClipEntry(entry)` 触发
 *     iOS 端 `pb.string = plainText` 覆盖 `pb.items` 双 MIME 的 bug
 */
expect suspend fun toRichClipEntry(
    plainText: String,
    html: String,
    config: RichTextHtmlConfig = RichTextHtmlConfig(),
): ClipEntry

/**
 * 把 rich text [ClipEntry] 实际写入系统剪贴板。
 *
 * **为什么需要这个 expect/actual(而不是直接调 `clipboard.setClipEntry(entry)`)?**
 *   - iOS 端 `toRichClipEntry(plain, html, config)` 内部已经**主动**调用
 *     `UIPasteboardBridge.setItems(plain, html)` 写入 `UIPasteboard.general.items` 双 MIME。
 *     但 Compose 库 `clipboard.setClipEntry(entry)` 在 iOS 端实现是
 *     `UIPasteboard.general.string = entry.plainText`,**会覆盖**前面写入的 `items` 数组
 *     (UIPasteboard 的 items 和 string 互斥,后写覆盖前写)。所以 iOS 端**必须跳过**
 *     `clipboard.setClipEntry`,否则 html 副 MIME 丢失,paste 时只能还原纯文本。
 *   - Android / JVM / Web 端 `toRichClipEntry` 内部虽然已经主动写了系统剪贴板(
 *     `ClipboardManager.setPrimaryClip` / `Toolkit.setContents` / `navigator.clipboard.write`),
 *     但 `clipboard.setClipEntry(entry)` **不会丢失**双 MIME(这些平台的双 MIME 写入是
 *     单一对象 ClipData / Transferable / ClipboardItem,不会被 string 覆盖),所以
 *     这些平台正常调 `clipboard.setClipEntry(entry)`:
 *       - 让 `RichTextClipboardManager` 之外的代码路径(比如直接调
 *         `clipboard.setClipEntry` 的其他测试)也能通过 FakeClipboard 拿到 entry
 *       - 部分平台 setPrimaryClip / setContents 是幂等的,重复写无害
 *
 * **iOS 端 iOS 平台特殊性**:
 *   iOS 端 `toRichClipEntry` 写完 `pb.items` 后,setRichTextClipEntry 是 **noop**,
 *   返回的 entry 仅作为"如果其他平台也调了 setClipEntry"的兜底占位。
 */
internal expect suspend fun setRichTextClipEntry(clipboard: Clipboard, entry: ClipEntry)

/**
 * 从剪贴板 [ClipEntry] 中读取纯文本并包装为 [AnnotatedString]。
 * 在 Web 平台上由于 [Clipboard.getClipEntry] 的异步特性,实现为 suspend,
 * 其他平台可同步读取,实际签名差异由编译器允许。
 */
expect suspend fun ClipEntry.toAnnotatedStringOrNull(): AnnotatedString?

/**
 * 富文本版 [toAnnotatedStringOrNull]:优先尝试读取 text/html → [com.taocent.simple.compose.component.richtext.core.internal.serialization.HtmlToAnnotatedString.parse]
 * 还原 spans / paragraph / emoji / hyperlink;失败 / 无 text/html 时返回 null,调用方应再 fallback 到 [toAnnotatedStringOrNull]。
 */
expect suspend fun ClipEntry.toRichAnnotatedStringOrNull(): AnnotatedString?

/**
 * Public 跨模块剪贴板写入函数 — 供 :richtext / :blockrichtext 内部 UI 调用。
 * 内部封装 emoji-id 缓冲协议,失败时通过 [RichTextState.reportError] 上报。
 */
suspend fun clipboardSetText(
    clipboard: Clipboard,
    state: RichTextState,
    text: AnnotatedString,
    htmlConfig: RichTextHtmlConfig = RichTextHtmlConfig(),
): PasteResult<Unit> {
    return RichTextClipboardManager(clipboard, state, state::reportError, htmlConfig).setText(text)
}

/**
 * Public 跨模块剪贴板读取函数 — 失败时通过 [RichTextState.reportError] 上报。
 */
suspend fun clipboardGetText(
    clipboard: Clipboard,
    state: RichTextState,
    htmlConfig: RichTextHtmlConfig = RichTextHtmlConfig(),
): PasteResult<AnnotatedString> {
    return RichTextClipboardManager(clipboard, state, state::reportError, htmlConfig).getText()
}
